import { RxReplicationState, startReplicationOnLeaderShip } from 'rxdb/plugins/replication';
import { addRxPlugin } from 'rxdb';
import { pendingPulls } from './syncState';
import { RxDBLeaderElectionPlugin } from 'rxdb/plugins/leader-election';
import { Subject } from 'rxjs';
import { useSyncStore } from '../../stores/sync.store';
import { isManualSyncing } from './replication';

addRxPlugin(RxDBLeaderElectionPlugin);

export const POSTGRES_INSERT_CONFLICT_CODE = "23505";
export const DEFAULT_MODIFIED_FIELD = 'updated_at';
export const DEFAULT_DELETED_FIELD = 'is_deleted';

// Pure helper function replacements to avoid relying on RxDB internal utility paths
const flatClone = <T>(obj: T): T => Object.assign({}, obj);
const lastOfArray = <T>(arr: T[]): T | undefined => arr[arr.length - 1];
const ensureNotFalsy = <T>(val: T | null | undefined): T => {
  if (val === null || val === undefined) {
    throw new Error('value is falsy');
  }
  return val;
};

export function isEqualIgnoringKeys(objA: any, objB: any, ignoreKeys: string[], allowedKeys?: Set<string>): boolean {
  const filterFn = (k: string) => !ignoreKeys.includes(k) && (!allowedKeys || allowedKeys.has(k));
  const keysA = Object.keys(objA || {}).filter(filterFn);
  const keysB = Object.keys(objB || {}).filter(filterFn);
  if (keysA.length !== keysB.length) return false;
  for (const key of keysA) {
    if (!keysB.includes(key)) return false;
    const valA = objA[key];
    const valB = objB[key];
    if (typeof valA === 'object' && valA !== null && typeof valB === 'object' && valB !== null) {
      if (JSON.stringify(valA) !== JSON.stringify(valB)) return false;
    } else if (valA !== valB) {
      return false;
    }
  }
  return true;
}

export function addDocEqualityToQuery(
  jsonSchema: any,
  deletedField: string,
  modifiedField: string,
  doc: any,
  query: any
) {
  const actualDeletedField = deletedField === '_deleted' || !deletedField ? 'is_deleted' : deletedField;
  const ignoreKeys = new Set([modifiedField, deletedField, actualDeletedField, '_deleted', '_meta', '_attachments', '_rev', 'sync_state']);
  const allowedKeys = new Set(Object.keys(jsonSchema.properties || {}));

  for (const key of Object.keys(doc)) {
    if (ignoreKeys.has(key) || !allowedKeys.has(key)) {
      continue;
    }
    const v = doc[key];
    const type = typeof v;
    if (type === "string" || type === "number") {
      query = query.eq(key, v);
    } else if (type === "boolean" || v === null) {
      query = query.is(key, v);
    } else if (type === 'undefined') {
      query = query.is(key, null);
    } else if (type === 'object') {
      // Ignore objects/arrays in doc equality since PostgREST cannot do .eq() comparison on JSONB/Arrays.
      continue;
    } else {
      throw new Error("unknown how to handle type: " + type);
    }
  }
  const schemaProps = jsonSchema.properties;
  for (const key of Object.keys(schemaProps)) {
    if (ignoreKeys.has(key) || Object.hasOwn(doc, key)) {
      continue;
    }
    query = query.is(key, null);
  }
  const isDeletedVal = Boolean(doc[actualDeletedField] ?? doc._deleted ?? false);
  query = query.eq(actualDeletedField, isDeletedVal);
  if (schemaProps[modifiedField]) {
    query = query.eq(modifiedField, doc[modifiedField]);
  }
  return query;
}

export class RxSupabaseReplicationState extends RxReplicationState<any, any> {
  override readonly replicationIdentifier: string;
  override readonly collection: any;
  override readonly pull: any;
  override readonly push: any;
  override readonly live: boolean;
  override readonly retryTime: number;
  override readonly autoStart: boolean;

  constructor(
    replicationIdentifier: string,
    collection: any,
    pull: any,
    push: any,
    live = true,
    retryTime = 1000 * 5,
    autoStart = true
  ) {
    super(replicationIdentifier, collection, '_deleted', pull, push, live, retryTime, autoStart);
    this.replicationIdentifier = replicationIdentifier;
    this.collection = collection;
    this.pull = pull;
    this.push = push;
    this.live = live;
    this.retryTime = retryTime;
    this.autoStart = autoStart;
  }
}

export function replicateSupabase(options: any) {
  options = flatClone(options);
  const collection = options.collection;
  const primaryPath = collection.schema.primaryPath;

  options.waitForLeadership = typeof options.waitForLeadership === 'undefined' ? true : options.waitForLeadership;
  options.live = typeof options.live === 'undefined' ? true : options.live;
  const modifiedField = options.modifiedField ? options.modifiedField : DEFAULT_MODIFIED_FIELD;
  const deletedField = options.deletedField ? options.deletedField : DEFAULT_DELETED_FIELD;
  const pullStream$ = new Subject<any>();
  let replicationPrimitivesPull: any;

  function rowToDoc(row: any) {
    const deleted = !!row[deletedField];
    const modified = row[modifiedField];
    const doc = flatClone(row);
    delete doc[deletedField];
    delete doc[modifiedField];
    doc._deleted = deleted;

    if (collection.schema.jsonSchema.properties[modifiedField]) {
      doc[modifiedField] = modified;
    }
    if (collection.schema.jsonSchema.properties[deletedField]) {
      doc[deletedField] = deleted;
    }
    if (collection.schema.jsonSchema.properties.price_tiers && !doc.price_tiers) {
      doc.price_tiers = [];
    }
    
    return doc;
  }

  async function fetchById(id: any) {
    try {
      const { data, error } = await options.client.from(options.tableName).select().eq(primaryPath, id).limit(1);
      if (error || !data || data.length !== 1) return null;
      return rowToDoc(data[0]);
    } catch {
      return null;
    }
  }

  if (options.pull) {
    replicationPrimitivesPull = {
      async handler(lastPulledCheckpoint: any, batchSize: number) {
        const syncStore = useSyncStore.getState();
        const canSync = syncStore.navigatorOnline && (syncStore.autoSyncEnabled || isManualSyncing());
        if (!canSync) {
          return {
            documents: [],
            checkpoint: lastPulledCheckpoint
          };
        }
        let query = options.client.from(options.tableName).select('*');
        if (options.pull.queryBuilder) {
          const maybeNewQuery = options.pull.queryBuilder({
            query,
            lastPulledCheckpoint,
            batchSize
          });
          if (maybeNewQuery) {
            query = maybeNewQuery;
          }
        }
        if (lastPulledCheckpoint) {
          const { modified, id } = lastPulledCheckpoint;
          const safeModified = new Date(modified).toISOString();
          query = query.or(`"${modifiedField}".gt.${safeModified},and("${modifiedField}".eq.${safeModified},"${primaryPath}".gt.${id})`);
        }

        query = query.order(modifiedField, {
          ascending: true
        }).order(primaryPath, {
          ascending: true
        }).limit(batchSize);

        const { data, error } = await query;
        if (error) {
          throw error;
        }
        const lastDoc = lastOfArray(data);
        const newCheckpoint = lastDoc ? {
          id: lastDoc[primaryPath],
          modified: lastDoc[modifiedField]
        } : undefined;
        const docs = data.map((row: any) => {
          const doc = rowToDoc(row);
          pendingPulls.add(doc[primaryPath]);
          // Remove from ignore list after 10 seconds to allow future manual edits
          setTimeout(() => pendingPulls.delete(doc[primaryPath]), 10000);
          return doc;
        });
        return {
          documents: docs,
          checkpoint: newCheckpoint
        };
      },
      batchSize: ensureNotFalsy(options.pull).batchSize,
      modifier: ensureNotFalsy(options.pull).modifier,
      stream$: pullStream$.asObservable(),
      initialCheckpoint: options.pull.initialCheckpoint
    };
  }

  let replicationPrimitivesPush: any;
  if (options.push) {
    replicationPrimitivesPush = {
      batchSize: options.push.batchSize || 100,
      initialCheckpoint: options.push.initialCheckpoint,
      modifier: options.push.modifier,
      async handler(rows: any[]) {
        if (!rows || rows.length === 0) return [];
        const syncStore = useSyncStore.getState();
        const canSync = syncStore.navigatorOnline && (syncStore.autoSyncEnabled || isManualSyncing());
        if (!canSync) {
          throw new Error('SyncPaused: Auto-sincronización pausada.');
        }

        const schemaProperties = collection.schema.jsonSchema.properties || {};
        const allowedKeys = new Set(Object.keys(schemaProperties));

        const newInserts: any[] = [];
        const updateRows: any[] = [];

        for (const row of rows) {
          // Ignorar ecos de documentos que ya están marcados como sincronizados
          if (row.newDocumentState?.sync_state === 'synced' && !row.assumedMasterState) {
            continue;
          }
          if (!row.assumedMasterState) {
            const raw = flatClone(row.newDocumentState);
            const doc: any = {};
            for (const k of Object.keys(raw)) {
              if (allowedKeys.has(k) && k !== 'sync_state' && k !== modifiedField && k !== '_deleted') {
                doc[k] = raw[k];
              }
            }
            if (raw._deleted) {
              doc[deletedField] = true;
            }
            newInserts.push(doc);
          } else {
            const ignoreKeys = ['sync_state', modifiedField, '_meta', '_rev', '_attachments'];
            if (isEqualIgnoringKeys(row.newDocumentState, row.assumedMasterState, ignoreKeys, allowedKeys)) {
              continue;
            }
            updateRows.push(row);
          }
        }

        const conflicts: any[] = [];

        // Batch Upsert for new documents in 1 single PostgREST HTTP request
        if (newInserts.length > 0) {
          const { error } = await options.client
            .from(options.tableName)
            .upsert(newInserts, { onConflict: primaryPath, ignoreDuplicates: false });

          if (error) {
            console.warn(`[Sync Push] Batch upsert on ${options.tableName} failed, falling back to itemized:`, error);
            for (const doc of newInserts) {
              try {
                const { error: singleErr } = await options.client.from(options.tableName).insert(doc);
                if (singleErr && singleErr.code === POSTGRES_INSERT_CONFLICT_CODE) {
                  const conflict = await fetchById(doc[primaryPath]);
                  if (conflict) conflicts.push(conflict);
                } else if (singleErr) {
                  console.error(`[Sync Push] Itemized insert error on ${options.tableName}:`, singleErr);
                  useSyncStore.getState().addError({
                    message: singleErr.message || `Error al insertar en ${options.tableName}`,
                    collection: options.tableName,
                  });
                } else {
                  try {
                    const qDoc = await collection.database.sync_queue.findOne({ selector: { id: doc[primaryPath] } }).exec();
                    if (qDoc) await qDoc.remove();
                  } catch (e) {}
                }
              } catch (e: any) {
                console.error(`[Sync Push] Fallback itemized insert exception on ${options.tableName}:`, e);
                useSyncStore.getState().addError({
                  message: e?.message || `Fallo de inserción en ${options.tableName}`,
                  collection: options.tableName,
                });
              }
            }
          } else {
            try {
              const ids = newInserts.map(d => d[primaryPath]);
              ids.forEach(id => {
                pendingPulls.add(id);
                setTimeout(() => pendingPulls.delete(id), 10000);
              });
              const qDocs = await collection.database.sync_queue.find({ selector: { id: { $in: ids } } }).exec();
              await Promise.all(qDocs.map((d: any) => d.remove()));
            } catch (e) {}
          }
        }

        // Process updates with optimism / conflict detection
        if (updateRows.length > 0) {
          for (const row of updateRows) {
            try {
              const id = row.newDocumentState[primaryPath];
              const raw = flatClone(row.newDocumentState);
              const toRow: any = {};
              for (const k of Object.keys(raw)) {
                if (allowedKeys.has(k) && k !== 'sync_state' && k !== modifiedField && k !== '_deleted') {
                  toRow[k] = raw[k];
                }
              }
              if (raw._deleted) {
                toRow[deletedField] = true;
              }

              let query = options.client.from(options.tableName).update(toRow);
              query = addDocEqualityToQuery(collection.schema.jsonSchema, deletedField, modifiedField, row.assumedMasterState, query);
              const { data, error } = await query.select();

              if (error) throw error;
              if (!data || data.length === 0) {
                const conflict = await fetchById(id);
                if (conflict) {
                  conflicts.push(conflict);
                } else {
                  console.warn(`[Sync Push] Registro ${id} no encontrado en ${options.tableName} durante update.`);
                  useSyncStore.getState().addError({
                    message: `Registro ${id} no encontrado en la nube al actualizar`,
                    collection: options.tableName,
                  });
                }
              } else {
                try {
                  const qDoc = await collection.database.sync_queue.findOne({ selector: { id } }).exec();
                  if (qDoc) await qDoc.remove();
                } catch (e) {}
              }
            } catch (err: any) {
              console.warn(`[Sync Push] Update conflict on ${options.tableName}:`, err);
              useSyncStore.getState().addError({
                message: err.message || `Error al actualizar ${options.tableName}`,
                collection: options.tableName,
              });
            }
          }
        }

        return conflicts;
      }
    };
  }

  const replicationState = new RxSupabaseReplicationState(
    options.replicationIdentifier,
    collection,
    replicationPrimitivesPull,
    replicationPrimitivesPush,
    options.live,
    options.retryTime,
    options.autoStart
  );

  if (options.live && options.pull && options.sharedChannel) {
    options.sharedChannel.on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: options.tableName
    }, (payload: any) => {
      if (!useSyncStore.getState().isOnline || payload.eventType === 'DELETE') {
        return;
      }
      const row = payload.new;
      const doc = rowToDoc(row);
      pendingPulls.add(doc[primaryPath]);
      setTimeout(() => pendingPulls.delete(doc[primaryPath]), 10000);
      pullStream$.next({
        checkpoint: {
          id: doc[primaryPath],
          modified: row[modifiedField]
        },
        documents: [doc]
      });
    });
  }

  if (options.live && options.pull) {
    const startBefore = replicationState.start.bind(replicationState);
    const cancelBefore = replicationState.cancel.bind(replicationState);
    replicationState.start = async () => {
      if (options.sharedChannel) {
        replicationState.cancel = () => {
          return cancelBefore();
        };
      } else {
        const sub = options.client.channel(options.replicationIdentifier).on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: options.tableName
        }, (payload: any) => {
          if (payload.eventType === 'DELETE') {
            return;
          }
          const row = payload.new;
          const doc = rowToDoc(row);
          pendingPulls.add(doc[primaryPath]);
          setTimeout(() => pendingPulls.delete(doc[primaryPath]), 10000);
          pullStream$.next({
            checkpoint: {
              id: doc[primaryPath],
              modified: row[modifiedField]
            },
            documents: [doc]
          });
        }).subscribe((status: any) => {
          if (status === 'SUBSCRIBED') {
            pullStream$.next('RESYNC');
          }
        });
        replicationState.cancel = () => {
          options.client.removeChannel(sub);
          return cancelBefore();
        };
      }
      await startBefore();
    };
  }
  startReplicationOnLeaderShip(options.waitForLeadership, replicationState);
  return replicationState;
}
